import type { GQL_ID } from '@@partner:types:graphql'
import type { Feedback, RsuiteSortType } from '@@partner:types:app'

import React, { useCallback, useState } from 'react'

import { Button, Avatar, Rate } from 'rsuite'
import { generatePath, Link } from 'react-router-dom'

import { NO_PHOTO_IMAGE } from '@@partner:config/image'
import { ROUTE_CUSTOMERS_VIEW_CUSTOMER, ROUTE_ORDERS_VIEW_ORDER } from '@@partner:config/routes'
import { usePartner } from '@@partner:helpers/hooks/auth'
import ResponsiveTable, { ResponsiveTableColumn } from '@components/common/ResponsiveTable/ResponsiveTable'

import './FeedbacksTable.scss'
import FeedbackModal from '../modal'
import { useAddFeedbackComment } from '@@partner:graphql/mutation/feedback/addFeedbackComment.gql'

export interface FeedbacksTableProps {
  loading: boolean
  feedbacks: Feedback[]
  generateOpenUrl?: (feedbackId: GQL_ID) => string
  sortColumn?: string
  sortType?: RsuiteSortType
  onSortColumn: (column: string, sortType?: RsuiteSortType) => void
}

export default function FeedbacksTable ({
  loading = false,
  feedbacks = [],
  generateOpenUrl,
  sortColumn,
  sortType,
  onSortColumn
}: FeedbacksTableProps) {
  const { brandSlug } = usePartner()
  const [ selectedFeedback, setSelectedFeedback ] = useState<Feedback | null>(null)
  const [ isModalOpen, setIsModalOpen ] = useState(false)

  const [ addFeedbackComment ] = useAddFeedbackComment()

  const handleOpenOrder = useCallback((orderId: GQL_ID) => {
    return generatePath(ROUTE_ORDERS_VIEW_ORDER, {
      brandSlug,
      orderId
    })
  }, [ brandSlug ])

  const handleOpenCustomer = useCallback((customerId: GQL_ID) => {
    return generatePath(ROUTE_CUSTOMERS_VIEW_CUSTOMER, {
      brandSlug,
      customerId
    })
  }, [ brandSlug ])

  const handleViewFeedback = useCallback((feedback: Feedback) => {
    setSelectedFeedback(feedback)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setSelectedFeedback(null)
  }, [])

  const handleCommentSubmit = useCallback(async (feedbackId: GQL_ID, comment: string) => {
    await addFeedbackComment({
      variables: {
        feedback_id: feedbackId,
        comment
      }
    })
  }, [ addFeedbackComment ])

  const columns: ResponsiveTableColumn<Feedback>[] = [
    {
      dataKey: 'id',
      header: 'ИД',
      sortable: true,
      flexGrow: 1,
      hideByDefault: true
    },
    {
      dataKey: 'order.id',
      header: 'Номер заказа',
      sortable: true,
      flexGrow: 1,
      render: (feedback) => (
        <div>
          {feedback.order_id ? (
            <Button as={Link} to={handleOpenOrder(feedback.order_id)} size="xs">
              {feedback.order_id}
            </Button>
          ) : '-'}
          {feedback.need_call && (
            <div style={{ color: 'red', fontSize: '11px', marginTop: '4px' }}>Требуется звонок</div>
          )}
        </div>
      )
    },
    {
      dataKey: 'images',
      header: 'Фото',
      flexGrow: 1,
      hideOnMobile: true,
      render: (feedback) => {
        const firstImage = feedback.images?.[0]
        return firstImage
          ? <Avatar src={firstImage.url ?? NO_PHOTO_IMAGE} size="lg" />
          : null
      }
    },
    {
      dataKey: 'customer_name',
      header: 'Пользователь',
      flexGrow: 2,
      isCardTitle: true,
      render: (feedback) => 
        <Link to={handleOpenCustomer(feedback.customer.id)} target="_blank" style={{ textDecoration: 'none' }}>
          <div>{`${feedback.customer?.first_name ?? ''} ${feedback.customer?.last_name?.substring(0,3) ?? ''}***`.trim() || '-'}</div>
          <a href={`tel:+${feedback.customer?.phone ?? ''}`} style={{ fontSize: '11px', color: '#555' }}>+{feedback.customer?.phone ?? ''}</a>
        </Link>
    },
    {
      dataKey: 'review',
      header: 'Отзыв',
      flexGrow: 3,
      render: (feedback) => (
        <span style={{
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'normal',
          fontSize: '12px'
        }}>
          {feedback.review || '—'}
        </span>
      )
    },
    {
      dataKey: 'shop.address',
      header: 'Магазин',
      sortable: true,
      flexGrow: 2,
      hideOnMobile: true,
      render: (feedback) => (
        <>
          {feedback?.shop?.name ? `${feedback.shop.name}, ` : ''}
          {feedback?.shop?.address ?? ''}
        </>
      )
    },
    {
      dataKey: 'general_rating',
      header: 'Рейтинг',
      sortable: true,
      flexGrow: 1,
      isCardBadge: true,
      render: (feedback) => (
        <Rate
          value={feedback.general_rating}
          readOnly
          className="FeedbacksTable__Rate"
          size="xs"
        />
      )
    },
  ]

  const renderActions = useCallback((feedback: Feedback) => (
    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
      <Button
        color="cyan"
        onClick={() => handleViewFeedback(feedback)}
        size="xs"
      >
        Посмотреть
      </Button>
      {feedback.order_id && (
        <Button
          color="blue"
          as={Link}
          to={handleOpenOrder(feedback.order_id)}
          size="xs"
        >
          Заказ
        </Button>
      )}
    </div>
  ), [ handleOpenOrder, handleViewFeedback ])

  return (
    <>
      <ResponsiveTable
        data={feedbacks}
        columns={columns}
        loading={loading}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSortColumn}
        renderActions={renderActions}
        rowHeight={70}
      />

      <FeedbackModal
        feedback={selectedFeedback}
        open={isModalOpen}
        onClose={handleCloseModal}
        onCommentSubmit={handleCommentSubmit}
      />
    </>
  )
}
